// ¿Se está mostrando el panel lateral y cuánto EMPUJA?
//
// Es la única fuente de verdad de esa respuesta, y la leen los dos lados del
// layout: `DashboardLayout` (para correrle el `right` al panel de contenido) y
// `PanelChatAtlas` (para dibujarse de ese mismo ancho). Que los dos llamen a
// este hook —y no cada uno a su propia cuenta— es lo que garantiza que la
// franja del chat y el hueco que le deja el contenido midan lo mismo.
//
// MOBILE NO EMPUJA. Correr el contenido 300px en una pantalla de 390 no deja
// nada que mirar, así que ahí el chat va a pantalla completa POR ENCIMA (ver
// `PanelChatAtlas`) y `ancho` vale 0: el contenido no se mueve ni un pixel.

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSidebar } from '../../context/SidebarContext';
import { useChatAtlasCascaron } from '../ChatAtlasContext';
import { anchoPanelChat, RUTA_CHAT_ATLAS } from './rutaChatAtlas';
import { useEsEscritorio } from './useEsEscritorio';

export interface EmpujeChat {
  /** El panel se dibuja (en desktop como franja, en mobile a pantalla completa). */
  visible: boolean;
  /** Además corre el contenido: sólo en desktop. */
  empuja: boolean;
  /** Px que hay que descontarle al contenido por derecha. 0 si no empuja. */
  ancho: number;
}

export function useEmpujeChat(): EmpujeChat {
  // El contexto CHICO, a propósito: este hook lo llama `DashboardLayout`, así
  // que leer el grande (con la conversación adentro) volvería a renderizar el
  // cascarón entero —sidebar incluida— con cada token del stream.
  const chat = useChatAtlasCascaron();
  const location = useLocation();
  const esEscritorio = useEsEscritorio();
  const { sidebarWidth } = useSidebar();
  const [anchoVentana, setAnchoVentana] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1440,
  );

  // En la pantalla dedicada el panel sobra: es la MISMA conversación y verla
  // dos veces sería confuso. Se esconde sin cerrar el chat, así al volver a
  // cualquier otra pantalla sigue abierto donde estaba — y el contenido de
  // `/chat` recupera el ancho completo.
  const enPantallaDedicada = location.pathname === RUTA_CHAT_ATLAS;
  const visible = chat.abierto && !enPantallaDedicada;
  const empuja = visible && esEscritorio;

  // El listener SÓLO existe mientras el chat empuja. Este hook lo llama
  // `DashboardLayout`, o sea que cada `setAnchoVentana` vuelve a renderizar el
  // cascarón entero: con el chat cerrado —el caso normal— eso sería un
  // re-render de toda la app por cada evento de resize, a cambio de un número
  // que en ese momento ni se usa (`ancho` vale 0). Al prenderse, el efecto
  // corre `alRedimensionar()` de una, así que el valor nunca entra viejo.
  useEffect(() => {
    if (!empuja || typeof window === 'undefined') return;
    const alRedimensionar = () => setAnchoVentana(window.innerWidth);
    alRedimensionar();
    window.addEventListener('resize', alRedimensionar);
    return () => window.removeEventListener('resize', alRedimensionar);
  }, [empuja]);

  return {
    visible,
    empuja,
    ancho: empuja ? anchoPanelChat(anchoVentana, sidebarWidth) : 0,
  };
}

export default useEmpujeChat;
